import React, {
  useState, useCallback, useEffect,
} from 'react';
import backendService from 'services/backend.service';
import { notifyOk, notifyError } from 'libs/utils/Toaster';
import ReferralAgreementsTable from './ReferralAgreementsTable';
import { IReferralAgreementData } from './ReferralAgreementForm';
import ApmModal, { IReferralTarget } from './ApmModal';
import { getApiErrorMessage } from './referralAgreementError';
import ApmEntityPage from './ApmEntityPage';
import ReferralReportingPage from './ReferralReportingPage';
import '../Referral.css';

type ViewType = 'table' | 'entityConfig';
type TabType = 'agreements' | 'reporting';

const ReferralAgreementsPage: React.FC = () => {
  const [view, setView] = useState<ViewType>('table');
  const [tab, setTab] = useState<TabType>('agreements');
  const [selectedAgreement, setSelectedAgreement] = useState<IReferralAgreementData | null>(null);
  const [modalAgreement, setModalAgreement] = useState<IReferralAgreementData | null | 'new'>('new');
  const [modalOpen, setModalOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState<number>(0);
  const [brands, setBrands] = useState<IReferralTarget[]>([]);
  const [connectors, setConnectors] = useState<IReferralTarget[]>([]);
  const [agreements, setAgreements] = useState<IReferralAgreementData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  const fetchAgreementsAndTargets = useCallback(async () => {
    setLoading(true);
    try {
      // Brands and connectors come straight from their own tables (not the feature
      // matrix), so an agreement can be set up before any integration exists.
      const [agreementsData, brandsData, connectorsData] = await Promise.all([
        backendService.getReferralAgreements(),
        backendService.getPaymentBrands(),
        backendService.getPaymentConnectors(),
      ]);

      const byName = (a: IReferralTarget, b: IReferralTarget) => a.name.localeCompare(b.name);
      const toTargets = (rows: any[]): IReferralTarget[] => rows
        .filter((r) => r?.id && r?.name)
        .map((r) => ({ id: r.id, name: r.name }))
        .sort(byName);

      setAgreements(agreementsData);
      setBrands(toTargets(brandsData));
      setConnectors(toTargets(connectorsData));
    } catch {
      notifyError('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAgreementsAndTargets();
  }, [refreshKey, fetchAgreementsAndTargets]);

  const handleCreate = () => {
    setModalAgreement(null);
    setModalOpen(true);
  };

  const handleEntityConfig = (agreement: IReferralAgreementData) => {
    setSelectedAgreement(agreement);
    setView('entityConfig');
  };

  const handleModalSave = useCallback(async (data: IReferralAgreementData) => {
    setLoading(true);
    try {
      const payload = {
        isActive: data.isActive,
        bips: Number(data.bips),
        invoicingCurrency: data.invoicingCurrency,
        xeroContactId: data.xeroContactId || null,
        refType: data.refType ?? 'GROSS_AMOUNT',
        period: data.period ?? 'MONTHLY',
        customDays: data.customDays ?? null,
        tiered: data.tiered ?? false,
        tiers: data.tiers ?? null,
      };

      // data.id covers the "APM Settings" flow from entity config page where
      // modalAgreement stays 'new'; modalAgreement.id covers the table-view edit flow
      const agreementId = data.id
        ?? (typeof modalAgreement === 'object' && modalAgreement?.id ? modalAgreement.id : null);
      const isEdit = !!agreementId;

      if (isEdit) {
        // What the agreement is attached to is fixed once created.
        await backendService.updateReferralAgreement(agreementId!, payload);
        notifyOk('Agreement updated successfully');
        if (selectedAgreement?.id === agreementId) {
          setSelectedAgreement({ ...selectedAgreement, ...data });
        }
      } else {
        await backendService.createReferralAgreement({
          ...payload,
          brandId: data.brandId || undefined,
          connectorId: data.connectorId || undefined,
        });
        notifyOk('Agreement created successfully');
      }

      setRefreshKey((prev) => prev + 1);
    } catch (error) {
      setLoading(false);
      notifyError(getApiErrorMessage(error, 'Failed to save agreement'));
      throw new Error('save failed');
    }
  }, [modalAgreement, selectedAgreement]);

  const handleAgreementUpdated = async (updated: IReferralAgreementData): Promise<void> => {
    setSelectedAgreement(updated);
    await handleModalSave(updated);
  };

  const handleToggleActive = useCallback(async (agreement: IReferralAgreementData) => {
    if (!agreement.id) {
      notifyError('Cannot update an agreement without an ID.');
      return;
    }
    const newIsActive = !agreement.isActive;
    setAgreements((prev) => prev.map((a) => (a.id === agreement.id ? { ...a, isActive: newIsActive } : a)));
    try {
      await backendService.updateReferralAgreement(agreement.id, { isActive: newIsActive });
      notifyOk(`Agreement ${newIsActive ? 'activated' : 'deactivated'} successfully`);
    } catch {
      setAgreements((prev) => prev.map((a) => (a.id === agreement.id ? { ...a, isActive: agreement.isActive } : a)));
      notifyError('Failed to update agreement status');
    }
  }, []);

  if (view === 'entityConfig' && selectedAgreement) {
    return (
      <div className="referral-container">
        <ApmEntityPage
          agreement={selectedAgreement}
          onAgreementUpdated={handleAgreementUpdated}
          onBack={() => setView('table')}
        />

        {modalOpen && (
          <ApmModal
            agreement={typeof modalAgreement === 'object' ? modalAgreement : null}
            brands={brands}
            connectors={connectors}
            onClose={() => { setModalOpen(false); setModalAgreement(null); }}
            onSave={handleModalSave}
          />
        )}
      </div>
    );
  }

  return (
    <div className="referral-container">
      <div className="referral-page-card">
        <div className="referral-page-header">
          <h1 className="referral-page-title">Referral Invoicing</h1>
          <div className="referral-top-tabs">
            <button
              className={`referral-top-tab${tab === 'agreements' ? ' referral-top-tab--active' : ''}`}
              onClick={() => setTab('agreements')}
              type="button"
            >
              Referral Agreements
            </button>
            <button
              className={`referral-top-tab${tab === 'reporting' ? ' referral-top-tab--active' : ''}`}
              onClick={() => setTab('reporting')}
              type="button"
            >
              Referral Reporting
            </button>
          </div>
        </div>

        <div className="referral-page-body">
          {tab === 'agreements' ? (
            <ReferralAgreementsTable
              agreements={agreements}
              loading={loading}
              onCreate={handleCreate}
              onEntityConfig={handleEntityConfig}
              onToggleActive={handleToggleActive}
            />
          ) : (
            <ReferralReportingPage agreements={agreements} />
          )}
        </div>
      </div>

      {modalOpen && (
        <ApmModal
          agreement={typeof modalAgreement === 'object' ? modalAgreement : null}
          brands={brands}
          connectors={connectors}
          onClose={() => { setModalOpen(false); setModalAgreement(null); }}
          onSave={handleModalSave}
        />
      )}
    </div>
  );
};

export default ReferralAgreementsPage;
